import { apiService } from './api';
import {
  LoginRequest,
  LoginResponse,
  SignupRequest,
  SignupResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ClaimTokenValidationResponseType,
  SetPasswordWithClaimTokenRequest,
  SetPasswordWithClaimTokenResponse,
  User,
} from '../types/auth';

/** Persisted user snapshot — excludes `userStats` (load via API when needed). */
export function serializeUserForLocalStorage(user: User): string {
  const { userStats: _omit, ...rest } = user;
  void _omit;
  return JSON.stringify(rest);
}

/** Syncs core user fields to localStorage; never stores `userStats`. */
export function persistUserToLocalStorage(user: User): void {
  localStorage.setItem('user', serializeUserForLocalStorage(user));
}

export class AuthService {
  private static instance: AuthService;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async signIn(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiService.post<LoginResponse, LoginRequest>(
        '/public/api/v1/auth/signIn',
        credentials,
      );

      // Store tokens in localStorage
      if (response.result?.accessToken) {
        localStorage.setItem('accessToken', response.result.accessToken);
        if (response.result.refreshToken) {
          localStorage.setItem('refreshToken', response.result.refreshToken);
        }
        persistUserToLocalStorage(response.result.user);
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  public async signUp(credentials: SignupRequest): Promise<SignupResponse> {
    try {
      const response = await apiService.post<SignupResponse, SignupRequest>(
        '/public/api/v1/auth/signUp',
        credentials,
      );

      // Store tokens and user data if present in signup response
      if (response.result?.accessToken) {
        localStorage.setItem('accessToken', response.result.accessToken);
        if (response.result.refreshToken) {
          localStorage.setItem('refreshToken', response.result.refreshToken);
        }
        if (response.result.user) {
          persistUserToLocalStorage(response.result.user);
        }
      }

      return response;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  public signOut(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Refresh access token using the stored refresh token.
   * Returns new tokens on success, null on failure.
   * Uses raw fetch to avoid triggering the axios 401 interceptor recursively.
   */
  public async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${baseUrl}/public/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const result = data?.result;

      if (result?.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
        if (result.refreshToken) {
          localStorage.setItem('refreshToken', result.refreshToken);
        }
        return {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken || '',
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  public getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  public getUser(): User | null {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  public async verifyAccount(token: string): Promise<void> {
    await apiService.post('/public/api/v1/auth/verifyAccount', { token });
  }

  public async forgotPassword(
    request: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> {
    try {
      const response = await apiService.post<
        ForgotPasswordResponse,
        ForgotPasswordRequest
      >('/public/api/v1/auth/sendForgetPasswordLink', request);
      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  public async resetPassword(
    request: ResetPasswordRequest,
  ): Promise<ResetPasswordResponse> {
    try {
      const response = await apiService.post<
        ResetPasswordResponse,
        ResetPasswordRequest
      >('/public/api/v1/auth/resetPasswordThroughLink', request);
      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  public async validateClaimToken(
    token: string,
  ): Promise<ClaimTokenValidationResponseType> {
    try {
      const response = await apiService.get<ClaimTokenValidationResponseType>(
        `/public/api/v1/auth/validate?token=${encodeURIComponent(token)}`,
      );
      return response;
    } catch (error) {
      console.error('Validate claim token error:', error);
      throw error;
    }
  }

  public async setPasswordWithClaimToken(
    request: SetPasswordWithClaimTokenRequest,
  ): Promise<SetPasswordWithClaimTokenResponse> {
    try {
      const response = await apiService.post<
        SetPasswordWithClaimTokenResponse,
        SetPasswordWithClaimTokenRequest
      >('/public/api/v1/auth/create-password', request);
      return response;
    } catch (error) {
      console.error('Set password with claim token error:', error);
      throw error;
    }
  }
}

export const authService = AuthService.getInstance();
